import React, { useRef, useState, useLayoutEffect, useEffect } from 'react';
import './WellDiagnosisChart.scss';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Dark from '@amcharts/amcharts5/themes/Dark';
import { generateMarkers } from '../../../utilities/chartUtils';
import { createCustomMarkerLegend } from '../../../utilities/chartHelpers';
import FullScreenIcon from '../../common/FullScreenIcon/FullScreenIcon';
import DownloadChart from '../../../components/time-series-chart/download/downloadChart';
import EmptyState from '../../../components/empty-state/EmptyState';
import { exportChart } from '../../../utilities/ExportChart';

interface SeriesConfigItem {
  key: string;
  name: string;
  color: string;
  shape?: string;
  selected?: boolean;
}

interface DiagnosisChartProps {
  chartData: any;
  seriesConfig: SeriesConfigItem[];
  widgetHeader: string;
  yAxisLabel?: string;
  xAxisLabel?: string;
  chartLabel?: string;
  invertYAxis?: boolean;
}

const DiagnosisChart: React.FC<DiagnosisChartProps> = ({
  chartData,
  seriesConfig,
  widgetHeader,
  xAxisLabel,
  yAxisLabel,
  chartLabel,
  invertYAxis = false,
}) => {
  const chartRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const am5RootRef = useRef<am5.Root | null>(null);

  // Fullscreen logic
  useEffect(() => {
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!containerRef.current) return;
    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
      });
    } else {
      document.exitFullscreen();
    }
  };

  const handleChartDownload = async (option: string) => {
    const element = contentRef.current;
    if (!element) return;
    await exportChart(element, getExportType(option), `DiagnosisChart-${widgetHeader}`, undefined, undefined, widgetHeader);
  };
  const getExportType = (option: string): 'JPG' | 'PDF' => (option === 'Download as PDF' ? 'PDF' : 'JPG');

  const hasAnySeries = seriesConfig.some(
    (cfg) => Array.isArray(chartData?.[cfg.key]) && chartData[cfg.key]?.length > 0,
  );

  useLayoutEffect(() => {
    if (!chartRef.current || !hasAnySeries) return;
    chartRef.current.innerHTML = '';
    if (am5RootRef.current) {
      try {
        am5RootRef.current.dispose();
      } catch {
        /* ignore */
      }
      am5RootRef.current = null;
    }
    const root = am5.Root.new(chartRef.current);
    am5RootRef.current = root;
    root.setThemes([am5themes_Dark.new(root)]);

    const chart = root.container.children.push(
      am5xy.XYChart.new(root, {
        panX: false,
        panY: false,
        layout: root.verticalLayout,
        paddingLeft: 10,
        paddingRight: 30,
        paddingTop: 20,
        paddingBottom: 10,
        background: am5.Rectangle.new(root, {
          fill: am5.color('#122031'),
          fillOpacity: 0,
        }),
      }),
    );

    // Axes
    const xAxisRenderer = am5xy.AxisRendererX.new(root, {
      minGridDistance: 50,
      strokeWidth: 1,
      strokeOpacity: 1,
      stroke: am5.color('#4A5463'),
    });
    const xAxis = chart.xAxes.push(am5xy.ValueAxis.new(root, { renderer: xAxisRenderer }));
    xAxisRenderer.labels.template.setAll({ fontSize: 12, fontFamily: 'Mulish', fontWeight: '400' });
    xAxis.children.moveValue(
      am5.Label.new(root, {
        text: xAxisLabel || 'Rate (bbl/min)',
        x: am5.percent(50),
        centerX: am5.percent(50),
        fontFamily: 'Mulish',
        fontSize: 12,
        fontWeight: '400',
        marginTop: 10,
      }),
      xAxis.children.length - 1,
    );

    const yAxisRenderer = am5xy.AxisRendererY.new(root, {
      strokeWidth: 1,
      strokeOpacity: 1,
      stroke: am5.color('#4A5463'),
      inversed: invertYAxis,
    });
    const yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, { renderer: yAxisRenderer ,
        min: invertYAxis ? undefined : 0,
        extraMin: invertYAxis ? 0.08 : 0,
      }));
    yAxisRenderer.labels.template.setAll({ fontSize: 12, fontFamily: 'Mulish', fontWeight: '400' });
    yAxis.children.unshift(
      am5.Label.new(root, {
        rotation: -90,
        text: yAxisLabel || 'Depth (ft)',
        y: am5.percent(50),
        centerX: am5.percent(50),
        textAlign: 'center',
        fontFamily: 'Mulish',
        fontSize: 12,
        fontWeight: '400',
        marginRight: -5,
      }),
    );

    // Grid
    const gridSettings = {
      stroke: am5.color('#616a77'),
      strokeWidth: 1,
      strokeOpacity: 0.3,
      visible: true,
      strokeDasharray: [2, 2],
    };
    yAxis.get('renderer').grid.template.setAll(gridSettings);
    xAxis.get('renderer').grid.template.setAll(gridSettings);

    // Series
    const legendSeries: any[] = [];
    const legendConfigForDisplay: Array<{ name: string; color: string; shape?: string }> = [];
    const allSeries: am5xy.LineSeries[] = [];
    const bullets: am5.Bullet[] = [];

    //cursor bullet
    const createCursorBullet = (series: am5xy.LineSeries, markerColor: string | undefined) => {
      const bullet = am5.Bullet.new(root, {
        sprite: am5.Circle.new(root, {
          radius: 6,
          fill: am5.color('#323F46'),
          stroke: am5.color(markerColor || '#60BFDA'),
          strokeWidth: 4,
          visible: false,
          x: -9999,
          y: -9999,
        }),
      });
      try {
        chart.plotContainer.children.push(bullet.get('sprite'));
        bullet.get('sprite')?.set('layer', 200);
      } catch {
        series.bulletsContainer.children.push(bullet.get('sprite'));
      }
      return bullet;
    };

    const getUnit = (label: string | undefined) => {
      if (!label) return '';
      const match = String(label).match(/\(([^)]+)\)/);
      return match && match[1] ? match[1] : '';
    };

    const toggleSeriesBullets = (series: any, visible: boolean) => {
      try {
        if (series.bulletsContainer) {
          series.bulletsContainer.set('visible', visible);
        }
      } catch {
        /* ignore */
      }
    };

    const doLayoutRetry = (tries = 2) => {
      try {
        const attempt = (remaining: number) => {
          try {
            if (root && typeof (root as any).layout === 'function') (root as any).layout();
            if (chart && chart.plotContainer && typeof (chart.plotContainer as any).markDirty === 'function') (chart.plotContainer as any).markDirty();
          } catch {
            /* ignore */
          }
          if (remaining > 0) requestAnimationFrame(() => attempt(remaining - 1));
        };
        attempt(tries);
      } catch {
        /* ignore */
      }
    };

    for (const cfg of seriesConfig) {
      const arr = chartData?.[cfg.key];
      const data =
        Array.isArray(arr) && arr.length > 0 ? arr.map((pair: number[]) => ({ x: pair[0], y: pair[1] })) : [];
      const series = chart.series.push(
        am5xy.LineSeries.new(root, {
          name: cfg.name,
          xAxis,
          yAxis,
          valueXField: 'x',
          valueYField: 'y',
          stroke: am5.color(cfg.color),
          fill: am5.color(cfg.color),
        }),
      );
 
      let displayName = (cfg.name || cfg.key || '').toString();
      const nl = displayName.toLowerCase();
      
      if (nl.includes('rate') && nl.includes('pwf')) {
        displayName = 'Rate vs Pwf history';
      } else if ((nl.includes('target') && nl.includes('pwf'))) {
        displayName = 'Target PWF';
      } else if (nl.includes('pip')) {
        displayName = 'Target PIP';
      } else if (nl.includes('gl vlps')) {
        displayName = 'GL VLP';
      }
      try {
        series.set('name', displayName);
      } catch {
        /* ignore */
      }
      series.strokes.template.setAll({ strokeWidth: 2 });
      // Tooltip
      try {
        series.set('tooltip', undefined);
      } catch {
        /* ignore */
      }
      const bullet = createCursorBullet(series, cfg.color);
      bullets.push(bullet);
      if (cfg.shape) {
        series.bullets.push((root) =>
          am5.Bullet.new(root, {
            sprite: generateMarkers(cfg.shape as string, cfg.color, 3, root),
          }),
        );
      } else {
        series.bullets.clear();
      }
      series.data.setAll(data);

      const nameLower = (cfg.name || cfg.key || '').toString().toLowerCase();
      const isRatePwf = nameLower.includes('rate') && nameLower.includes('pwf');
      const isGlVlps = nameLower.includes('vlps') || nameLower.includes('gl vlps') || nameLower.includes('gl_vlps') || nameLower.includes('glvlps');
      let visible = true;
      if (typeof cfg.selected === 'boolean') {
        visible = cfg.selected;
      } else if (isRatePwf || isGlVlps) {
        visible = false;
      }
      series.set('visible', !!visible);
      try {
        toggleSeriesBullets(series, !!visible);
      } catch {
        /* ignore */
      }
      legendSeries.push(series);
      legendConfigForDisplay.push({ name: displayName, color: cfg.color, shape: cfg.shape });
      allSeries.push(series);
    }

    createCustomMarkerLegend(chart, root, legendSeries, legendConfigForDisplay, true);

    // Rescale X axis to only consider visible series
    const rescaleXAxisToVisible = () => {
      try {
        let minX = Number.POSITIVE_INFINITY;
        let maxX = Number.NEGATIVE_INFINITY;
        for (const s of allSeries) {
          try {
            if (!s.get('visible')) continue;
            const values = s.data.values as any[];
            for (const v of values) {
              if (v && typeof v.x === 'number') {
                if (v.x < minX) minX = v.x;
                if (v.x > maxX) maxX = v.x;
              }
            }
          } catch {
            /* ignore */
          }
        }

        if (minX !== Number.POSITIVE_INFINITY && maxX !== Number.NEGATIVE_INFINITY) {
          // add a small padding (5%) so points are not on the very edge
          const pad = (maxX - minX) * 0.05;
          // If all X values are positive, start axis at 0
          if (minX >= 0) {
            const minToUse = 0;
            const maxToUse = pad === 0 ? maxX + 1 : maxX + pad;
            if (pad === 0) {
              xAxis.setAll({ min: minToUse, max: maxToUse });
            } else {
              xAxis.set('min', minToUse);
              xAxis.set('max', maxToUse);
            }
          } else {
            if (pad === 0) {
              xAxis.setAll({ min: minX - 1, max: maxX + 1 });
            } else {
              xAxis.set('min', minX - pad);
              xAxis.set('max', maxX + pad);
            }
          }
        } else {
          // reset to auto
          xAxis.set('min', undefined);
          xAxis.set('max', undefined);
        }
      } catch {
        /* ignore */
      }
    };

    const rescaleYAxisToVisible = () => {
      try {
        let minY = Number.POSITIVE_INFINITY;
        let maxY = Number.NEGATIVE_INFINITY;
        for (const s of allSeries) {
          try {
            if (!s.get('visible')) continue;
            const values = s.data.values as any[];
            for (const v of values) {
              if (v && typeof v.y === 'number') {
                if (v.y < minY) minY = v.y;
                if (v.y > maxY) maxY = v.y;
              }
            }
          } catch {
            /* ignore */
          }
        }

        if (minY !== Number.POSITIVE_INFINITY && maxY !== Number.NEGATIVE_INFINITY) {
          const pad = (maxY - minY) * 0.12;
          if (minY >= 0) {
            const minToUse = 0;
            const maxToUse = pad === 0 ? maxY + Math.max(1, Math.abs(maxY) * 0.02) : maxY + pad;
            if (pad === 0) {
              yAxis.setAll({ min: minToUse, max: maxToUse });
            } else {
              yAxis.set('min', minToUse);
              yAxis.set('max', maxToUse);
            }
          } else {
            if (pad === 0) {
              const extra = Math.max(1, Math.abs(maxY) * 0.02);
              yAxis.setAll({ min: minY - extra, max: maxY + extra });
            } else {
              yAxis.set('min', minY - pad);
              yAxis.set('max', maxY + pad);
            }
          }
        } else {
          yAxis.set('min', undefined);
          yAxis.set('max', undefined);
        }
      } catch {
        /* ignore */
      }
    };

    // Listen to series visibility changes
    try {
      for (const s of allSeries) {
        try {
          s.on('visible', () => {
              try {
                const isVisible = !!s.get('visible');
                toggleSeriesBullets(s, isVisible);
              } catch {
                /* ignore */
              }
            rescaleXAxisToVisible();
            rescaleYAxisToVisible();
          });
        } catch {
          /* ignore */
        }
      }
    } catch {
      /* ignore */
    }

    // initial rescale according to default visibility
    rescaleXAxisToVisible();
    rescaleYAxisToVisible();

    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(root, {
        xAxis: xAxis,
      }),
    );
    cursor.lineX.setAll({ visible: false });
    cursor.lineY.setAll({ visible: false });

    try {
      for (const s of allSeries) {
        try {
          const isVisible = !!s.get('visible');
          toggleSeriesBullets(s, isVisible);
        } catch {
          /* ignore */
        }
      }
      doLayoutRetry(2);
    } catch {
      /* ignore */
    }

    cursor.events.on('cursormoved', () => {
      const pos = cursor.getPrivate('positionX');
      if (typeof pos !== 'number' || pos < 0 || pos > 1) {
        bullets.forEach((b) => b.get('sprite').set('visible', false));
        return;
      }
      const xValue = xAxis.positionToValue(pos);
      let bestIdx = -1;
      let bestDist = Infinity;
      const perSeriesClosest: Array<{ idx: number; closest: any; dist: number }> = [];

      allSeries.forEach((series, idx) => {
        if (!series.get('visible')) {
          perSeriesClosest.push({ idx, closest: null, dist: Infinity });
          return;
        }
        const data = series.data.values as any[];
        let closest: any = null;
        let minDist = Infinity;
        for (const pt of data) {
          const dist = Math.abs(pt.x - xValue);
          if (dist < minDist) {
            minDist = dist;
            closest = pt;
          }
        }
        perSeriesClosest.push({ idx, closest, dist: minDist });

        if (closest && minDist < bestDist) {
          bestDist = minDist;
          bestIdx = idx;
        }
      });

      // show bullet only for the best (nearest) series; hide others
      allSeries.forEach((_s, idx) => {
        const sprite = bullets[idx].get('sprite');
        if (idx === bestIdx && perSeriesClosest[idx]?.closest) {
          const closest = perSeriesClosest[idx].closest;
          const xPos = xAxis.valueToPosition(closest.x);
          const yPos = yAxis.valueToPosition(closest.y);
          const xPx = xAxis.get('renderer').positionToCoordinate(xPos);
          const yPx = yAxis.get('renderer').positionToCoordinate(yPos);
          sprite.set('x', xPx);
          sprite.set('y', yPx);
          sprite.set('visible', true);
        } else {
          sprite.set('visible', false);
        }
      });
    });
    cursor.events.on('cursorhidden', () => {
      bullets.forEach((b) => b.get('sprite').set('visible', false));
    });

   const onPlotPointerDown = () => {
    try {
      const pos = cursor.getPrivate('positionX');
      if (typeof pos !== 'number' || pos < 0 || pos > 1) return;
      
      const xValue = xAxis.positionToValue(pos);

      let bestIdx = -1;
      let bestClosest: any = null;
      let bestDist = Infinity;

      allSeries.forEach((series, idx) => {
        if (!series.get('visible')) return;

        const data = series.data.values as any[];
        for (const pt of data) {
          const dist = Math.abs(pt.x - xValue);
          if (dist < bestDist) {
            bestDist = dist;
            bestIdx = idx;
            bestClosest = pt;
          }
        }
      });

    if (bestIdx < 0 || !bestClosest) return;

    // Toggle same point
    const key = `${bestIdx}:${bestClosest.x}`;
    if ((chart as any)._lastShownTooltipKey === key) {
      chartRef.current?.querySelector('.diagnosis-click-tooltip')?.remove();
      allSeries.forEach((s) => toggleSeriesBullets(s, !!s.get('visible')));
      (chart as any)._lastShownTooltipKey = undefined;
      return;
    }

    allSeries.forEach((_s, idx) => {
      const sprite = bullets[idx]?.get('sprite');
      if (!sprite) return;

      if (idx === bestIdx) {
        const xPos = xAxis.valueToPosition(bestClosest.x);
        const yPos = yAxis.valueToPosition(bestClosest.y);
        const xPx = xAxis.get('renderer').positionToCoordinate(xPos);
        const yPx = yAxis.get('renderer').positionToCoordinate(yPos);

        sprite.set('x', xPx);
        sprite.set('y', yPx);
        sprite.set('visible', true);
      } else {
        sprite.set('visible', false);
      }
    });
    
    const series = allSeries[bestIdx];
    const xUnit = getUnit(xAxisLabel);
    const yUnit = getUnit(yAxisLabel);

    const xPos = xAxis.valueToPosition(bestClosest.x);
    const yPos = yAxis.valueToPosition(bestClosest.y);

    const xPx = xAxis.get('renderer').positionToCoordinate(xPos);
    const yPx = yAxis.get('renderer').positionToCoordinate(yPos);

    chartRef.current?.querySelector('.diagnosis-click-tooltip')?.remove();

    const tooltipEl = document.createElement('div');
    const formatNumber = (v: number) => v.toFixed(2);
    tooltipEl.className = 'diagnosis-click-tooltip';
    tooltipEl.innerHTML = `
      <strong>${formatNumber(bestClosest.x)}${xUnit ? ' ' + xUnit : ''}</strong><br/>
      ${series.get('name')}: ${formatNumber(bestClosest.y)}${yUnit ? ' ' + yUnit : ''}
    `;

    Object.assign(tooltipEl.style, {
      position: 'absolute',
      left: `${xPx}px`,
      top: `${yPx - 10}px`,
      transform: 'translate(-50%, -100%)',
      background: '#001023',
      color: '#fff',
      padding: '8px 10px',
      borderRadius: '6px',
      boxShadow: '0 2px 6px rgba(0,0,0,0.4)',
      zIndex: '9999',
      pointerEvents: 'none',
    });
    
    chartRef.current?.appendChild(tooltipEl);

    const chartRect = chartRef.current!.getBoundingClientRect();
    let leftPx = chartRect.left + xPx;
    const topPx = chartRect.top + yPx - 10;
    const tooltipRect = tooltipEl.getBoundingClientRect();
    const minLeft = chartRect.left + tooltipRect.width / 2 + 8;
    const maxLeft = chartRect.right - tooltipRect.width / 2 - 8;

    leftPx = Math.max(minLeft, Math.min(leftPx, maxLeft));

    Object.assign(tooltipEl.style, {
      position: 'fixed',
      left: `${leftPx}px`,
      top: `${topPx}px`,
      transform: 'translate(-50%, -100%)',
    });
 
    (chart as any)._lastShownTooltipKey = key;
    } catch {
      /* ignore */
    }
  };

    chart.plotContainer.events.on('pointerdown', onPlotPointerDown);

    const hideHtmlTooltipAndReset = () => {
      try {
        chartRef.current?.querySelector('.diagnosis-click-tooltip')?.remove();
        
        allSeries.forEach((s) => {
          toggleSeriesBullets(s, !!s.get('visible'));
        });
        
        (chart as any)._lastShownTooltipKey = undefined;
      } catch {
        /* ignore */
      }
    };

    chart.plotContainer.events.on('pointerout', hideHtmlTooltipAndReset);
    // Click outside to hide tooltips
    const onDocPointerDown = (ev: any) => {
      try {
        const target = ev.target as Node;
        if (!chartRef.current) return;
        if (!chartRef.current.contains(target)) {
          // remove any existing HTML tooltip
          try {
            const existing = chartRef.current?.querySelector('.diagnosis-click-tooltip');
            if (existing) existing.remove();
          } catch {
            /* ignore */
          }
          // restore bullets/markers visibility according to each series' visible flag
          allSeries.forEach((s) => {
            try {
              const shouldShow = !!s.get('visible');
              toggleSeriesBullets(s, shouldShow);
            } catch {
              /* ignore */
            }
          });
          (chart as any)._lastShownTooltipKey = undefined;
        }
      } catch {
        // ignore
      }
    };
    document.addEventListener('pointerdown', onDocPointerDown);

    chart.appear(1000, 100);
    return () => {
      try {
        try {
          chart.plotContainer.events.off('pointerdown', onPlotPointerDown as any);
        } catch {
          /* ignore */
        }
        try {
          document.removeEventListener('pointerdown', onDocPointerDown as any);
        } catch {
          /* ignore */
        }
        try {
          chart.plotContainer.events.off('pointerout', hideHtmlTooltipAndReset as any);
        } catch {
          /* ignore */
        }
      } catch {
        // ignore
      }

      if (am5RootRef.current) {
        try {
          am5RootRef.current.dispose();
        } catch {
          console.error('Error disposing am5 root');
        }
        am5RootRef.current = null;
      }
    };
  }, [chartData, hasAnySeries]);

  return (
    <section
      ref={containerRef}
      className={`ogl-chart-container diagnosis-chart-container${isFullscreen ? ' fullscreen' : ''}`}
    >
      <header className='ogl-chart-header'>
        <div className='chart-title'>
          <h2>{widgetHeader}</h2>
        </div>
        <div className='chart-actions'>
          <button
            analytics-id='diagnosis-chart-fullscreen-btn'
            className='chart-action-button'
            onClick={toggleFullscreen}
          >
            <FullScreenIcon />
          </button>
          <button analytics-id='diagnosis-chart-download-btn' className='chart-action-button'>
            <DownloadChart onClick={handleChartDownload} canExportJpg={true} canExportPdf={true} />
          </button>
        </div>
      </header>
      <div className='chart-divider'></div>
      <div className='diagnosis-chart-label'>{chartLabel ?? ''}</div>
      <div ref={contentRef} className='diagnosis-chart-content'>
        {hasAnySeries ? (
          <div ref={chartRef} className='chart-area' />
        ) : (
          <div className='diagnosis-empty-chart-container'>
            <EmptyState message='No data available' />
          </div>
        )}
      </div>
    </section>
  );
};

export default DiagnosisChart;
